import {
	DefaultBorderColor,
	ActiveBorderColor,
	Header,
} from '@dealstack/frontend/shared/components/AccountSettings/Header';
import { StepCircleIcon } from '@dealstack/frontend/shared/components/StepCircleIcon/StepCircleIcon';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Text, Button, Flex, Box } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { TextInput } from 'react-hook-form-mantine';
import {
	VERIFICATION_CODE_LENGTH,
	VerificationCode,
} from '@dealstack/frontend/shared/components/VerificationCode/VerificationCode';
import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import {
	changeEmail,
	deleteEmailReq,
	requestChangeEmail,
} from '@dealstack/frontend/shared/dataExchange/services/auth.service';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { getUserProfile } from '@dealstack/frontend/shared/dataExchange/services/user.service';
import type { SectionEditMode } from '@dealstack/frontend/shared/components/AccountSettings/AccountSettings.types';
import { getBackendErrorCode } from '@dealstack/frontend/shared/dataExchange/services/backendCodedError';
import { ERROR_CODES } from '@dealstack/shared/lib/common/errors';
import { useSelector } from 'react-redux';
import { CommonReduxState } from '@dealstack/frontend/shared/globalConfig';

enum CHANGE_EMAIL_REQUEST_FORM {
	CURRENT_PASSWORD = 'current_password',
	NEW_EMAIL = 'new_email',
}

interface ChangeEmailRequestForm {
	[CHANGE_EMAIL_REQUEST_FORM.CURRENT_PASSWORD]: string;
	[CHANGE_EMAIL_REQUEST_FORM.NEW_EMAIL]: string;
}

interface Props extends SectionEditMode {
	email: string;
	canChangeEmail: boolean;
	authenticationFactorId?: string;
}

export const ManageEmailAddress = ({
	email,
	canChangeEmail,
	authenticationFactorId,
	isEditModeEnabled,
	setIsEditModeEnabled,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const numberOfVerificationCodeInputs = VERIFICATION_CODE_LENGTH;
	const [verificationCode, setVerificationCode] = useState('');
	const [verificationCodeError, setVerificationCodeError] = useState('');
	const [totpCode, setTotpCode] = useState('');
	const [totpCodeError, setTotpCodeError] = useState('');
	const [isTotpCodeDemanded, setIsTotpCodeDemanded] = useState(false);

	const applicationType = useSelector((state: CommonReduxState) => state.global.applicationType);

	const [isResendDisabled, setIsResendDisabled] = useState(false);
	const [hasRequestEmailVerification, setHasRequestEmailVerification] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const commonYup = useCommonYupValidation();
	const requestChangeEmailRequest = useWrappedRequest(requestChangeEmail);
	const changeEmailRequest = useWrappedRequest(changeEmail);
	const deleteEmailRequest = useWrappedRequest(deleteEmailReq);
	const getUserProfilesRequest = useQuery({
		queryKey: [API_PATHS.USER_PROFILE_GET],
		queryFn: ({ signal }) => getUserProfile(API_PATHS.USER_PROFILE_GET, signal),
	});
	const validationSchema = Yup.object().shape({
		[CHANGE_EMAIL_REQUEST_FORM.NEW_EMAIL]: commonYup.email.required.notOneOf(
			[email],
			tCommonUi('profile.account_settings.email.error.same_as_current')
		),
		[CHANGE_EMAIL_REQUEST_FORM.CURRENT_PASSWORD]: commonYup.string.required,
	});

	const changeEmailRequestForm = useForm<ChangeEmailRequestForm>({
		mode: 'all',
		reValidateMode: 'onChange',
		defaultValues: {
			[CHANGE_EMAIL_REQUEST_FORM.CURRENT_PASSWORD]: '',
			[CHANGE_EMAIL_REQUEST_FORM.NEW_EMAIL]: '',
		},
		resolver: yupResolver(validationSchema),
	});

	const newEmail = changeEmailRequestForm.watch(CHANGE_EMAIL_REQUEST_FORM.NEW_EMAIL);

	const isTotpCodeShown = !!authenticationFactorId || isTotpCodeDemanded;
	const isTotpCodeComplete = !isTotpCodeShown || totpCode.length === VERIFICATION_CODE_LENGTH;

	const onSubmitChangeEmailRequest = () => {
		const currentPassword = changeEmailRequestForm.getValues(
			CHANGE_EMAIL_REQUEST_FORM.CURRENT_PASSWORD
		);
		setTotpCodeError('');
		requestChangeEmailRequest
			.fetch({
				newEmail,
				password: currentPassword,
				totpCode: totpCode.length > 0 ? totpCode : undefined,
				applicationType,
			})
			.onSuccess(() => {
				setHasRequestEmailVerification(true);
			})
			.onAnyError((error: unknown) => {
				const code = getBackendErrorCode(error);
				if (code === ERROR_CODES.AUTH.CREDENTIALS[50013]) {
					setIsTotpCodeDemanded(true);
					setTotpCodeError(tCommonUi('profile.account_settings.totp.missing_code'));
					return;
				}
				if (code === ERROR_CODES.AUTH.CREDENTIALS[50014]) {
					setIsTotpCodeDemanded(true);
					setTotpCodeError(tCommonUi('profile.account_settings.totp.invalid_code'));
					return;
				}
				const message = error instanceof Error ? error.message : '';
				if (message.includes('Invalid credentials')) {
					const errorTitle = tCommonUi(
						'profile.account_settings.password.current_password_incorrect'
					);
					changeEmailRequestForm.setError(CHANGE_EMAIL_REQUEST_FORM.CURRENT_PASSWORD, {
						type: 'manual',
						message: errorTitle,
					});
				}
			});
	};

	const onSubmitEmailCodeVerification = () => {
		changeEmailRequest
			.fetch({
				code: verificationCode,
			})
			.onSuccess(async () => {
				await getUserProfilesRequest.refetch();
				setIsEditModeEnabled(false);
				showToastV2({
					title: tCommonUi('profile.account_settings.email.toast.success.title'),
					message: tCommonUi('profile.account_settings.email.toast.success.message'),
				});
			})
			.onAnyError(() => {
				setVerificationCodeError(tCommonUi('profile.account_settings.email.invalid_code'));
				setIsResendDisabled(false);
			});
	};

	const handleResend = () => {
		setVerificationCode('');
		if (isTotpCodeShown) {
			setTotpCode('');
			setTotpCodeError('');
			setHasRequestEmailVerification(false);
			return;
		}
		setIsResendDisabled(true);
		onSubmitChangeEmailRequest();
	};

	const resetForm = () => {
		changeEmailRequestForm.reset();
		setVerificationCode('');
		setTotpCode('');
		setTotpCodeError('');
		setIsTotpCodeDemanded(false);
	};

	const cancelEmailRequest = async () => {
		if (newEmail && hasRequestEmailVerification) {
			await deleteEmailRequest.fetch({ newEmail }).concludeWithPromise();
		}
	};

	const onHeaderClick = async () => {
		setHasRequestEmailVerification(false);
		setIsEditModeEnabled(!isEditModeEnabled);
		resetForm();
	};

	// Effect to delete the current email request when the user navigates away from the component or presses cancel
	useEffect(() => {
		if (!hasRequestEmailVerification) {
			return;
		}
		return () => {
			void cancelEmailRequest();
		};
	}, [hasRequestEmailVerification]);

	const title = isEditModeEnabled
		? tCommonUi('profile.account_settings.email.edit_header')
		: tCommonUi('profile.account_settings.email.header');

	const headerActionName = isEditModeEnabled
		? tCommonUi('profile.account_settings.cancel')
		: tCommonUi('profile.account_settings.email.change_email');

	const form = (
		<>
			<div className="flex flex-row gap-4 items-start">
				<StepCircleIcon isEnabled={!hasRequestEmailVerification} />

				{!hasRequestEmailVerification ? (
					<FormProvider {...changeEmailRequestForm}>
						<form
							onSubmit={changeEmailRequestForm.handleSubmit(
								onSubmitChangeEmailRequest
							)}
							className="flex flex-col gap-6 w-full"
						>
							<div className="flex flex-col gap-4 pt-1">
								<TextInput
									name={CHANGE_EMAIL_REQUEST_FORM.NEW_EMAIL}
									control={changeEmailRequestForm.control}
									size="md"
									placeholder={tCommonUi(
										'profile.account_settings.email.placeholder'
									)}
									label={tCommonUi('profile.account_settings.email.labelV2')}
									type="email"
									data-testid={
										TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_EMAIL_NEW_EMAIL
									}
									styles={{
										error: {
											fontSize: '14px !important',
										},
									}}
								/>
								<TextInput
									name={CHANGE_EMAIL_REQUEST_FORM.CURRENT_PASSWORD}
									control={changeEmailRequestForm.control}
									size="md"
									placeholder={tCommonUi(
										'profile.account_settings.password.current_password_placeholder'
									)}
									label={tCommonUi(
										'profile.account_settings.password.current_password'
									)}
									type={showPassword ? 'text' : 'password'}
									rightSection={
										<Icon
											variant={showPassword ? 'unview-v3' : 'view-v3'}
											onClick={() => {
												setShowPassword(!showPassword);
											}}
											size="xl"
										/>
									}
									data-testid={
										TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_EMAIL_CURRENT_PASSWORD
									}
									styles={{
										error: {
											fontSize: '14px !important',
										},
									}}
								/>
								{isTotpCodeShown && (
									<div
										className="flex flex-col gap-2"
										data-testid={
											TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_EMAIL_TOTP_CODE
										}
									>
										<Text size="B6Medium" c="dark.8">
											{tCommonUi('profile.account_settings.totp.label')}
										</Text>
										<Text size="B6Medium" c="dark.0">
											{tCommonUi('profile.account_settings.totp.description')}
										</Text>
										<VerificationCode
											value={totpCode}
											onChange={setTotpCode}
											errorMessage={totpCodeError}
											autoFocus={false}
										/>
									</div>
								)}
							</div>
							<Button
								type="submit"
								disabled={
									!changeEmailRequestForm.formState.isValid ||
									!isTotpCodeComplete ||
									requestChangeEmailRequest.isLoading
								}
								variant="primary"
								style={{
									height: '36px',
									width: 'fit-content',
									marginBottom: '16px',
								}}
								data-testid={
									TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_EMAIL_VERIFICATION_CODE_REQUEST_BUTTON
								}
							>
								{tCommonUi(
									'profile.account_settings.email.request_verification_code'
								)}
							</Button>
						</form>
					</FormProvider>
				) : (
					<Text size="B6Medium" c="gray.8" className="mb-4 pt-1">
						{tCommonUi('profile.account_settings.email.labelV2')}
					</Text>
				)}
			</div>
			<div className="flex flex-row gap-4 items-start">
				<StepCircleIcon isEnabled={hasRequestEmailVerification} />
				{!hasRequestEmailVerification ? (
					<Text size="B6Medium" c="gray.8" className="pt-1">
						{tCommonUi('profile.account_settings.email.check_email_for_code')}
					</Text>
				) : (
					<Flex direction="column" gap="1rem" w="100%">
						<VerificationCode
							numberOfVerificationCodeInputs={numberOfVerificationCodeInputs}
							label={
								<Box mb="1rem">
									<label
										data-testid={
											TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_EMAIL_LABEL_TEXT
										}
									>
										{`${tCommonUi(
											'profile.account_settings.email.verify_code_label'
										)} ${newEmail}`}
									</label>
								</Box>
							}
							value={verificationCode}
							onChange={setVerificationCode}
							errorMessage={verificationCodeError}
						/>
						<Flex gap="1rem" w="100%">
							<Button
								onClick={onSubmitEmailCodeVerification}
								variant="primary"
								disabled={
									!verificationCode ||
									verificationCode.length < numberOfVerificationCodeInputs
								}
								style={{ height: '36px', width: 'fit-content' }}
								data-testid={
									TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_EMAIL_VERIFY_CODE_BUTTON
								}
							>
								{tCommonUi('profile.account_settings.email.verify_code')}
							</Button>
							<Button
								type="button"
								variant="link-color"
								style={{ height: '36px', width: 'fit-content' }}
								disabled={isResendDisabled}
								onClick={handleResend}
								data-testid={
									TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_EMAIL_RESEND_BUTTON
								}
							>
								{tCommonUi('profile.account_settings.email.resend')}
							</Button>
						</Flex>
					</Flex>
				)}
			</div>
		</>
	);

	if (changeEmailRequest.isLoading || requestChangeEmailRequest.isLoading) {
		return <FullscreenLoader name="pageLoading" />;
	}

	return (
		<div className="flex flex-col">
			<Header
				mainTitle={title}
				action={headerActionName}
				onClick={onHeaderClick}
				canPerformAction={canChangeEmail}
				data-testid={TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_EMAIL_BUTTON}
				isEditModeEnabled={isEditModeEnabled}
			/>
			<div
				className="flex flex-col p-4 border-solid border-r border-b border-l rounded-b-lg w-full"
				style={{
					borderColor: isEditModeEnabled ? ActiveBorderColor : DefaultBorderColor,
				}}
			>
				{isEditModeEnabled ? (
					form
				) : (
					<Text
						size="B6Medium"
						c="dark.8"
						data-testid={
							TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_EMAIL_CURRENT_EMAIL_TEXT
						}
					>
						{email}
					</Text>
				)}
			</div>
		</div>
	);
};
